import { flattenEntities, projectCoachSnapshot } from "./gamecraft";

function spriteOf(project, id) {
  return (project?.sprites || []).find((s) => s.id === id);
}

function clamp(n) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function entityCraftScore(e) {
  let n = 0;
  if ((e.name || "").trim() && e.name !== "Nueva idea" && e.name !== "New idea") n += 20;
  if ((e.role || "").trim()) n += 12;
  if (e.subtype) n += 12;
  if (e.linked_sprite_id) n += 20;
  if ((e.notes || "").trim().length > 8) n += 12;
  if ((e.hp ?? 0) > 0 && (e.damage ?? 0) >= 0) n += 12;
  if ((e.tags || []).length) n += 6;
  if (e.linked_file_id || e.linked_audio_id) n += 6;
  return clamp(n);
}

export function studioHealth(project) {
  const ents = flattenEntities(project);
  const snap = projectCoachSnapshot(project);
  const sprites = project?.sprites || [];
  const files = (project?.files || []).filter((f) => /\.js$/i.test(f.path || ""));
  const samples = project?.samples || [];
  const pads = project?.soundboard || [];
  const stories = project?.story_items || [];
  const notes = project?.notes || [];
  const idea = (project?.gdd?.idea || "").trim();
  const objective = (project?.gdd?.objective || "").trim();
  const playlogs = project?.config?.playtest_log || [];
  const cards = (project?.kanban?.columns || []).flatMap((c) => c.cards || []);

  const ideasScore = ents.length
    ? clamp(ents.reduce((s, e) => s + entityCraftScore(e), 0) / ents.length)
    : 15;
  const storyScore = stories.length
    ? clamp(20 + Math.min(40, stories.length * 8) + (stories.some((s) => (s.body || "").length > 20) ? 25 : 0) + (objective ? 15 : 0))
    : (objective ? 25 : 8);
  const spriteScore = sprites.length
    ? clamp(25 + Math.min(40, sprites.length * 4) + (ents.filter((e) => e.linked_sprite_id).length / Math.max(1, ents.length)) * 35)
    : 10;
  const audioScore = clamp(
    (samples.length ? 35 : 0) + (pads.some((p) => p.data_url || p.clip_id) ? 25 : 10) + Math.min(30, samples.length * 5) + (snap.missing.audio ? 0 : 10)
  );
  const codeScore = files.length ? clamp(30 + Math.min(50, files.length * 12) + (files.some((f) => (f.content || "").length > 40) ? 20 : 0)) : 12;
  const engineScore = clamp(
    (playlogs.length ? 40 : 12) + (project?.config?.playtest?.creatureId || project?.config?.playtest?.weaponId ? 20 : 0) + (snap.missing.hero ? 0 : 20) + (snap.missing.enemy ? 0 : 20)
  );
  const projectScore = clamp((idea ? 40 : 10) + (objective ? 25 : 0) + (project?.genre ? 15 : 0) + ((project?.references || []).length ? 20 : 10));
  const notesScore = notes.length ? clamp(30 + Math.min(50, notes.length * 10) + (notes.some((n) => (n.links || []).length) ? 20 : 0)) : 10;
  const canvasScore = cards.length ? clamp(25 + Math.min(50, cards.length * 6)) : 12;

  const modules = [
    { id: "project", label: "Proyecto", score: projectScore, tab: "project" },
    { id: "ideas", label: "Ideas", score: ideasScore, tab: "ideas" },
    { id: "story", label: "Historia", score: storyScore, tab: "story" },
    { id: "sprites", label: "Sprites", score: spriteScore, tab: "sprites" },
    { id: "audio", label: "Audio", score: audioScore, tab: "audio" },
    { id: "code", label: "Código", score: codeScore, tab: "code" },
    { id: "canvas", label: "Canvas", score: canvasScore, tab: "canvas" },
    { id: "engine", label: "Motor", score: engineScore, tab: "engine" },
    { id: "notes", label: "Notas", score: notesScore, tab: "notes" },
  ];
  const overall = clamp(modules.reduce((s, m) => s + m.score, 0) / modules.length);

  const next = [];
  if (!idea) next.push({ tab: "project", label: "Escribe la idea en una frase" });
  if (snap.missing.hero) next.push({ tab: "ideas", label: "Define el héroe (HP, daño, sprite)" });
  if (snap.missing.enemy) next.push({ tab: "ideas", label: "Añade un enemigo con telegráfo" });
  if (snap.missing.weapon) next.push({ tab: "ideas", label: "Enlaza un arma al playtest" });
  if (snap.missing.sprites) next.push({ tab: "sprites", label: "Dibuja o importa el primer sprite 32px" });
  if (snap.missing.audio) next.push({ tab: "audio", label: "3 SFX: salto, golpe, pickup" });
  if (!files.length) next.push({ tab: "code", label: "Abre mechanics.js y prueba un hook" });
  if (!playlogs.length) next.push({ tab: "engine", label: "Dale a Run y pausa para el log" });
  if (!next.length) next.push({ tab: "engine", label: "Playtest y recorta 1 cosa del scope" });

  return { overall, modules, next: next.slice(0, 4), snapshot: snap };
}

export function gradeLabel(score) {
  if (score >= 96) return { letter: "S", blurb: "11/10 — se puede amar" };
  if (score >= 90) return { letter: "A", blurb: "Nivel estudio" };
  if (score >= 78) return { letter: "B+", blurb: "Ya se puede amar" };
  if (score >= 65) return { letter: "B", blurb: "Vertical slice" };
  if (score >= 50) return { letter: "C+", blurb: "Prototipo vivo" };
  if (score >= 35) return { letter: "C", blurb: "Kit de demo" };
  return { letter: "D", blurb: "Empezando" };
}

/** Elenco que el motor va a jugar: héroe, enemigo, arma, piso, SFX. */
export function studioCast(project) {
  const ents = flattenEntities(project);
  const sprites = project?.sprites || [];
  const pt = project?.config?.playtest || {};
  const hero = ents.find((e) => e.subtype === "hero") || ents.find((e) => e.kind === "characters");
  const enemy = ents.find((e) => ["mobs", "enemies", "minibosses", "bosses"].includes(e.kind));
  const weapon = ents.find((e) => e.kind === "weapons") || ents.find((e) => /weapon|arma|sword/i.test(`${e.subtype || ""} ${e.role || ""}`));
  const heroSpr = spriteOf(project, pt.creatureId) || spriteOf(project, hero?.linked_sprite_id)
    || sprites.find((s) => /hero/i.test(s.name || "")) || sprites.find((s) => (s.studio || "creatures") === "creatures");
  const enemySpr = spriteOf(project, enemy?.linked_sprite_id)
    || sprites.find((s) => /slime|enemy|mob/i.test(s.name || ""))
    || sprites.find((s) => s.id !== heroSpr?.id && (s.studio || "creatures") === "creatures");
  const weaponSpr = spriteOf(project, pt.weaponId) || spriteOf(project, weapon?.linked_sprite_id)
    || sprites.find((s) => s.studio === "gear" || /sword|arma|weapon/i.test(`${s.name || ""} ${s.category || ""}`));
  const floorSpr = spriteOf(project, pt.floorId)
    || sprites.find((s) => s.studio === "world" || /piso|tile|floor/i.test(s.name || ""));
  const pads = project?.soundboard || [];
  const samples = project?.samples || [];
  const tags = new Set(
    [...pads.map((p) => p.tag || p.id), ...samples.map((s) => s.tag)].filter(Boolean).map(String)
  );
  const sfx = { jump: tags.has("jump"), hit: tags.has("hit") || tags.has("hurt"), pickup: tags.has("pickup") };
  const loopIds = new Set([heroSpr?.id, enemySpr?.id, weaponSpr?.id, floorSpr?.id].filter(Boolean));
  const linkedFileIds = new Set([hero?.linked_file_id, enemy?.linked_file_id, weapon?.linked_file_id].filter(Boolean));
  const ready = !!(hero && enemy && heroSpr && (sfx.jump || sfx.hit || samples.length));
  return {
    hero, enemy, weapon, heroSpr, enemySpr, weaponSpr, floorSpr, sfx, loopIds, linkedFileIds, ready,
    members: [
      { role: "Héroe", ent: hero, spr: heroSpr, tab: "ideas", kind: "hero" },
      { role: "Enemigo", ent: enemy, spr: enemySpr, tab: "ideas", kind: "enemy" },
      { role: "Arma", ent: weapon, spr: weaponSpr, tab: "sprites", kind: "weapon" },
    ],
  };
}

export function matchesFocus(focus, { entityId, spriteId, fileId, audioId, storyId } = {}) {
  if (!focus) return false;
  if (focus.entityId && entityId && focus.entityId === entityId) return true;
  if (focus.spriteId && spriteId && focus.spriteId === spriteId) return true;
  if (focus.fileId && fileId && focus.fileId === fileId) return true;
  if (focus.audioId && audioId && focus.audioId === audioId) return true;
  if (focus.storyId && storyId && focus.storyId === storyId) return true;
  if (focus.id && (focus.id === entityId || focus.id === spriteId || focus.id === fileId || focus.id === audioId || focus.id === storyId)) return true;
  return false;
}
